import { Flex, message, Modal, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { CustomButton } from '@/components/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteBooking, updateBooking } from '@/api/booking';
import './index.scss';
import dayJs from 'dayjs';
import { BookingStatus, BookingStatusEnum, UserRole } from '@/types';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectUser } from '@/slice/user';
import { ROUTES } from '@/types/routes';

import { notifyActionError } from '@/helpers/subscriptionError';
export const CancelBooking = (props: any) => {
  const queryClient = useQueryClient();
  const isConsultant = useAppSelector(selectUser).role === UserRole.CONSULTANT;

  const sessionMutation = useMutation({
    mutationKey: ['update-session'],
    mutationFn: () => {
      return isConsultant
        ? updateBooking(props.bookingId, {
            status: BookingStatusEnum.DECLINED,
            duration: 15,
            dateTime: dayJs().toDate(),
          })
        : deleteBooking(props.bookingId);
    },
    onSuccess: () => {
      message.success('Meeting canceled successfully!');
      queryClient.invalidateQueries({
        queryKey: ['bookings'],
      });
      // TCX-790: also refresh the scoped booking DETAIL (['booking', ...]).
      // Prefix-matches every user's detail key without needing the userId here.
      queryClient.invalidateQueries({
        queryKey: ['booking'],
      });
    },
    onError: (error) => {
      // TCX-900: distinguishes a retryable Stripe outage from a rejection.
      notifyActionError(error, 'Failed to cancel meeting. Something went wrong.');
      queryClient.invalidateQueries({
        queryKey: ['bookings'],
      });
      queryClient.invalidateQueries({
        queryKey: ['booking'],
      });
    },
  });

  const onFinish = () => {
    const currentLocation = window.location.pathname;
    sessionMutation.mutate();

    setTimeout(() => {
      if (!isConsultant && currentLocation !== ROUTES.MY_BOOKINGS) {
        props.onCancel();
        window.location.replace(ROUTES.MY_BOOKINGS);
      }

      props.onCancel();
    }, 1000);
  };

  return (
    <Modal
      closeIcon={<CloseOutlined />}
      open={props.open}
      onCancel={props.onCancel}
      width={460}
      centered
      footer={null}
      className={'cancel-booking-modal'}
      styles={{
        mask: {
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
      }}
      {...props}
    >
      <Flex vertical className={'header'} align={'center'} gap={16}>
        <Typography.Text className={'title'}>
          Are you sure you want to cancel meeting?
        </Typography.Text>

        <Typography.Text className={'description'}>
          Canceling this meeting will remove it from your pending meetings and
          remove the calendar invites on both your calendar and the{' '}
          {isConsultant ? "Franchisor's" : "Consultant's"} calendar.
        </Typography.Text>
      </Flex>

      <Flex vertical gap={12}>
        <CustomButton onClick={onFinish} type={'primary'} text={'CONFIRM'} />

        <CustomButton
          type={'default'}
          onClick={props.onCancel}
          text={'CANCEL'}
        />
      </Flex>
    </Modal>
  );
};
